"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdjudicatedRowAdditionSchema = void 0;
exports.encodeAdjudicationLedgerWrites = encodeAdjudicationLedgerWrites;
exports.toResolutionDecisions = toResolutionDecisions;
exports.toAddedRows = toAddedRows;
const z = require("zod");
const consensus_types_1 = require("../../../../Consensus/consensus.types");
const package_data_adjudication_types_1 = require("../../../../PackageDataAdjudication/package-data-adjudication.types");
const slice_key_1 = require("../../../../Consensus/slice-key");
const json_value_1 = require("../../../../Utils/json-value");
const adjudication_admitted_rows_1 = require("./adjudication-admitted-rows");
const adjudication_slice_1 = require("./adjudication-slice");
const adjudication_roster_1 = require("./adjudication-roster");
const adjudication_step_types_1 = require("./adjudication-step.types");
function encodeAdjudicationLedgerWrites(input, output, attribution, options = {}) {
    const slice = (0, adjudication_slice_1.adjudicationSliceOf)(input, options.paramUnitsResolver);
    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
    const applicable = output.groundTruthDecisions.filter((decision) => (0, adjudication_step_types_1.isGroundTruthRuled)(decision) && !isSuppressed(decision.anchor, absentRowKeys));
    const settledExistences = settledRowExistences(input, output);
    const cascadedRenames = cascadedRenameDecisions(input, output, options.excludedRowKeys ?? new Set());
    const ruledCells = [...applicable, ...cascadedRenames.groundTruth];
    const assertions = ruledCells.map((decision) => ({
        address: addressOf(input, decision.anchor),
        datum: datumOf(decision, input, output, slice),
        clauses: decision.clauses ?? [],
        comment: decision.comment ?? null,
        attribution,
    }));
    const ruledWrong = withoutRedundantErrors([
        ...output.errorDecisions.filter(adjudication_step_types_1.isErrorRuled),
        ...cascadedErrorDecisions(input, output, slice),
        ...cascadedRenames.errors,
    ], assertions, input, slice);
    const errorStatements = ruledWrong.map((decision) => ({
        address: addressOf(input, decision.anchor),
        datum: errorDatumOf(decision.anchor, decision.rawValue, input, output, slice, settledExistences),
        clauses: decision.clauses ?? [],
        errorCodeIds: decision.errorCodeIds ?? [],
        comment: decision.comment ?? null,
        attribution,
    }));
    return {
        assertions: [...assertions, ...impliedRowAssertions(input, output, ruledCells, attribution)],
        errorStatements,
    };
}
function withoutRedundantErrors(decisions, assertions, input, slice) {
    const taken = new Set(assertions.flatMap((assertion) => assertion.datum.kind === consensus_types_1.AnchorKind.Field ?
        [cellKey(assertion.address.anchor, assertion.datum.value.normalized)]
        : []));
    return decisions.filter((decision) => {
        if (decision.anchor.kind !== consensus_types_1.AnchorKind.Field) {
            return true;
        }
        const key = cellKey(decision.anchor, (0, adjudication_slice_1.normalizedCellValueOf)(decision.rawValue, decision.anchor, input, slice));
        if (taken.has(key)) {
            return false;
        }
        taken.add(key);
        return true;
    });
}
function toResolutionDecisions(input, output) {
    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
    return output.groundTruthDecisions
        .filter((decision) => (0, adjudication_step_types_1.isGroundTruthRuled)(decision) && !isSuppressed(decision.anchor, absentRowKeys))
        .map((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Row ?
        {
            sliceKey: input.sliceKey,
            anchor: decision.anchor,
            shouldExist: decision.existence === package_data_adjudication_types_1.RowExistence.Present,
            isOverride: true,
        }
        : {
            sliceKey: input.sliceKey,
            anchor: decision.anchor,
            value: decision.rawValue,
            isOverride: true,
        });
}
exports.AdjudicatedRowAdditionSchema = z.object({
    sliceKey: slice_key_1.SliceKeySchema,
    rowKey: z.string(),
    values: z.record(z.string(), json_value_1.JsonValueSchema),
});
function toAddedRows(input, output) {
    const projectedRowKeys = new Set(input.rows.map((row) => row.rowKey));
    const admitted = output.groundTruthDecisions.filter((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Row &&
        decision.existence === package_data_adjudication_types_1.RowExistence.Present &&
        !projectedRowKeys.has(decision.anchor.rowKey));
    return admitted
        .flatMap((decision) => {
        const content = rowContentOf(input, output, decision.anchor.rowKey);
        return isRowContent(content) ? [{ rowKey: decision.anchor.rowKey, values: content }] : [];
    })
        .sort((left, right) => left.rowKey < right.rowKey ? -1
        : left.rowKey > right.rowKey ? 1
            : 0)
        .map(({ rowKey, values }) => ({ sliceKey: input.sliceKey, rowKey, values }));
}
function isRowContent(content) {
    return typeof content === 'object' && content !== null && !Array.isArray(content);
}
function cascadedErrorDecisions(input, output, slice) {
    const reasons = (0, adjudication_step_types_1.absentRowReasons)(output);
    if (reasons.size === 0) {
        return [];
    }
    const alreadyRuled = new Set(output.errorDecisions.map((decision) => cellKey(decision.anchor, (0, adjudication_slice_1.normalizedCellValueOf)(decision.rawValue, decision.anchor, input, slice))));
    return [...reasons.keys()].sort().flatMap((rowKey) => {
        const reason = reasons.get(rowKey);
        if (reason === undefined) {
            return [];
        }
        return condemnedValues(input, rowKey, slice)
            .filter((cell) => !alreadyRuled.has(cellKey({ kind: consensus_types_1.AnchorKind.Field, rowKey, column: cell.column }, cell.normalized)))
            .map((cell) => ({
            anchor: { kind: consensus_types_1.AnchorKind.Field, rowKey, column: cell.column },
            rawValue: cell.rawValue,
            clauses: reason.clauses,
            errorCodeIds: reason.errorCodeIds,
            comment: reason.comment,
        }));
    });
}
function cascadedRenameDecisions(input, output, excludedRowKeys) {
    const column = (0, adjudication_roster_1.ruledSignalColumnOf)(input);
    const renames = (0, adjudication_roster_1.effectiveSignalDecisions)(input, output).filter((decision) => decision.kind === adjudication_step_types_1.SignalDispositionKind.Rename && (0, adjudication_roster_1.isSignalRuled)(decision));
    if (column === null || renames.length === 0) {
        return { groundTruth: [], errors: [] };
    }
    const nameById = new Map(input.roster.map((signal) => [signal.signalId, signal.name]));
    const absentRowKeys = (0, adjudication_step_types_1.rowsRuledAbsent)(output);
    const directlyRuled = new Set(output.groundTruthDecisions.flatMap((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Field && decision.anchor.column === column ? [decision.anchor.rowKey] : []));
    const cascaded = { groundTruth: [], errors: [] };
    for (const rename of renames) {
        const previousName = rename.signalId === undefined ? undefined : nameById.get(rename.signalId);
        if (previousName === undefined || rename.name === undefined) {
            continue;
        }
        const previousKey = (0, adjudication_roster_1.signalIdentityKey)(previousName);
        for (const row of [...input.rows].sort((left, right) => compareRowKeys(left.rowKey, right.rowKey))) {
            const held = row.values[column];
            if (typeof held !== 'string' || (0, adjudication_roster_1.signalIdentityKey)(held) !== previousKey) {
                continue;
            }
            if (absentRowKeys.has(row.rowKey) || directlyRuled.has(row.rowKey) || excludedRowKeys.has(row.rowKey)) {
                continue;
            }
            const anchor = { kind: consensus_types_1.AnchorKind.Field, rowKey: row.rowKey, column };
            cascaded.groundTruth.push({ anchor, rawValue: rename.name, clauses: [], comment: null });
            cascaded.errors.push({
                anchor,
                rawValue: held,
                clauses: rename.clauses ?? [],
                errorCodeIds: rename.errorCodeIds ?? [],
                comment: rename.comment ?? null,
            });
        }
    }
    return cascaded;
}
function compareRowKeys(left, right) {
    return (left < right ? -1
        : left > right ? 1
            : 0);
}
function condemnedValues(input, rowKey, slice) {
    const keyColumns = new Set(slice.keyColumnNames());
    const held = Object.entries(input.rows.find((row) => row.rowKey === rowKey)?.values ?? {}).map(([column, rawValue]) => ({ column, rawValue }));
    const proposed = input.contested.flatMap((value) => (value.anchor.kind === consensus_types_1.AnchorKind.Field &&
        value.anchor.rowKey === rowKey &&
        value.proposed.kind === consensus_types_1.AnchorKind.Field) ?
        [{ column: value.anchor.column, rawValue: value.proposed.value.raw }]
        : []);
    const byKey = new Map();
    for (const cell of [...held, ...proposed]) {
        if (keyColumns.has(cell.column)) {
            continue;
        }
        const normalized = (0, adjudication_slice_1.normalizedCellValueOf)(cell.rawValue, { kind: consensus_types_1.AnchorKind.Field, rowKey, column: cell.column }, input, slice);
        const key = `${cell.column}\x1F${JSON.stringify(normalized)}`;
        if (!byKey.has(key)) {
            byKey.set(key, { ...cell, normalized });
        }
    }
    return [...byKey.entries()]
        .sort(([left], [right]) => left < right ? -1
        : left > right ? 1
            : 0)
        .map(([, cell]) => cell);
}
function cellKey(anchor, normalized) {
    return `${(0, consensus_types_1.anchorKey)(anchor)}\x1F${JSON.stringify(normalized)}`;
}
function impliedRowAssertions(input, output, ruled, attribution) {
    const explicitRows = new Set(ruled.flatMap((decision) => (decision.anchor.kind === consensus_types_1.AnchorKind.Row ? [decision.anchor.rowKey] : [])));
    const impliedRows = new Set(ruled.flatMap((decision) => decision.anchor.kind === consensus_types_1.AnchorKind.Field && !explicitRows.has(decision.anchor.rowKey) ?
        [decision.anchor.rowKey]
        : []));
    return [...impliedRows].sort().map((rowKey) => ({
        address: addressOf(input, { kind: consensus_types_1.AnchorKind.Row, rowKey }),
        datum: {
            kind: consensus_types_1.AnchorKind.Row,
            existence: package_data_adjudication_types_1.RowExistence.Present,
            rowContentRaw: rowContentOf(input, output, rowKey),
        },
        clauses: [],
        comment: null,
        attribution,
    }));
}
function datumOf(decision, input, output, slice) {
    if (decision.anchor.kind === consensus_types_1.AnchorKind.Row) {
        const existence = decision.existence ?? package_data_adjudication_types_1.RowExistence.Present;
        return {
            kind: consensus_types_1.AnchorKind.Row,
            existence,
            rowContentRaw: existence === package_data_adjudication_types_1.RowExistence.Absent ? null : rowContentOf(input, output, decision.anchor.rowKey),
        };
    }
    const raw = decision.rawValue ?? null;
    return {
        kind: consensus_types_1.AnchorKind.Field,
        value: { raw, normalized: (0, adjudication_slice_1.normalizedCellValueOf)(raw, decision.anchor, input, slice) },
    };
}
function rowContentOf(input, output, rowKey) {
    const projected = input.rows.find((row) => row.rowKey === rowKey)?.values;
    if (projected !== undefined) {
        return projected;
    }
    return (0, adjudication_admitted_rows_1.admittedRowContentOf)(input, output, rowKey);
}
function errorDatumOf(anchor, rawValue, input, output, slice, settledExistences) {
    if (anchor.kind === consensus_types_1.AnchorKind.Row) {
        const settled = settledExistences.get(anchor.rowKey);
        if (settled === undefined) {
            throw new Error(`Row '${anchor.rowKey}' was ruled wrong without its ground truth being settled, so there is ` +
                'no claim for the ruling to contradict.');
        }
        const existence = settled === package_data_adjudication_types_1.RowExistence.Present ? package_data_adjudication_types_1.RowExistence.Absent : package_data_adjudication_types_1.RowExistence.Present;
        return {
            kind: consensus_types_1.AnchorKind.Row,
            existence,
            rowContentRaw: existence === package_data_adjudication_types_1.RowExistence.Absent ? null : rowContentOf(input, output, anchor.rowKey),
        };
    }
    return {
        kind: consensus_types_1.AnchorKind.Field,
        value: { raw: rawValue ?? null, normalized: (0, adjudication_slice_1.normalizedCellValueOf)(rawValue, anchor, input, slice) },
    };
}
function settledRowExistences(input, output) {
    const settled = new Map();
    for (const standing of input.standings) {
        const datum = standing.groundTruthDatum;
        if (standing.anchor.kind !== consensus_types_1.AnchorKind.Row || datum === null || datum.kind !== consensus_types_1.AnchorKind.Row) {
            continue;
        }
        settled.set(standing.anchor.rowKey, datum.existence);
    }
    for (const decision of output.groundTruthDecisions) {
        if (decision.anchor.kind === consensus_types_1.AnchorKind.Row && decision.existence !== undefined) {
            settled.set(decision.anchor.rowKey, decision.existence);
        }
    }
    return settled;
}
function addressOf(input, anchor) {
    return {
        packageId: input.packageId,
        sliceKey: input.sliceKey,
        sliceVersion: input.sliceVersion,
        anchor,
    };
}
function isSuppressed(anchor, absentRowKeys) {
    return anchor.kind === consensus_types_1.AnchorKind.Field && absentRowKeys.has(anchor.rowKey);
}
//# sourceMappingURL=adjudication-encode.js.map